import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { drawWeatherWidget, parseWeatherObservation, weatherSummary, type NoticeDataWidgetLayer, type NoticeWidgetData } from '@cornerstone/shared';
import { API_BASE } from '../client/api';
import { contentGroupHeaders } from '../client/groupScope';

type Entry = { source: string; data: NoticeWidgetData };
export type WeatherPreviewCache = Map<string, Entry>;
export const WeatherPreviewContext = createContext<WeatherPreviewCache | null>(null);
const sourceKey = (layer: NoticeDataWidgetLayer) => JSON.stringify([API_BASE, contentGroupHeaders(), layer.source]);
export function cachedWeatherPreview(cache: WeatherPreviewCache | null, layer: NoticeDataWidgetLayer): NoticeWidgetData | null {
  const entry = cache?.get(layer.id);
  return entry?.source === sourceKey(layer) ? entry.data : null;
}
const unavailable = (message: string): NoticeWidgetData => ({ status: 'unavailable', title: '날씨', lines: [], sourceLabel: '', updatedAt: null, message });

export function WeatherWidgetCanvas({ layer, data }: { layer: NoticeDataWidgetLayer; data: NoticeWidgetData | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const node = canvas.current, ctx = node?.getContext('2d');
    if (!node || !ctx) return;
    const scale = Math.min(1, 2048 / Math.max(layer.width, layer.height));
    node.width = Math.max(1, Math.round(layer.width * scale)); node.height = Math.max(1, Math.round(layer.height * scale));
    ctx.scale(scale, scale); drawWeatherWidget(ctx, { ...layer, x: 0, y: 0 }, data);
  }, [layer, data]);
  return <canvas ref={canvas} role="img" aria-label={`날씨 미리보기 · ${weatherSummary(data)}`} style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none', objectFit: 'contain' }} />;
}

export function WeatherWidgetPreview({ layer }: { layer: NoticeDataWidgetLayer }) {
  const cache = useContext(WeatherPreviewContext), source = sourceKey(layer);
  const [result, setResult] = useState<Entry | null>(null);
  useEffect(() => {
    let active = true, requestVersion = 0;
    let controller: AbortController | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let timer: ReturnType<typeof setTimeout>;
    const update = (data: NoticeWidgetData) => {
      if (!active) return;
      const entry = { source, data }; cache?.set(layer.id, entry); setResult(entry);
    };
    const poll = async () => {
      const ticket = ++requestVersion, request = new AbortController(); controller = request;
      const requestTimeout = setTimeout(() => request.abort(), 15000); timeout = requestTimeout;
      const current = () => active && ticket === requestVersion;
      try {
        const body: NoticeDataWidgetLayer = { id: layer.id, kind: 'dataWidget', source: layer.source, title: '날씨',
          x: 0, y: 0, width: 600, height: 320, z: 0, opacity: 1, rotation: 0, fontSize: 24, color: '#000000', backgroundColor: null };
        const response = await fetch(`${API_BASE}/api/notices/widget-preview`, { method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...contentGroupHeaders() }, signal: request.signal, body: JSON.stringify(body) });
        if (!response.ok) throw new Error(response.status === 400 ? '지역 설정을 확인해 주세요.' : '날씨를 불러올 수 없습니다.');
        const data = await response.json() as NoticeWidgetData;
        if (!current()) return;
        const weather = parseWeatherObservation(data.weather);
        if (weather && ['ready', 'stale'].includes(data.status)) update({ ...data, weather });
        else update(unavailable(data.status === 'unavailable' ? data.message || '날씨 정보가 없습니다.' : '날씨 데이터 형식을 확인해 주세요.'));
      } catch { if (current()) update(unavailable('지역 설정이나 네트워크 연결을 확인해 주세요.')); }
      finally { clearTimeout(requestTimeout); if (current()) { controller = null; timer = setTimeout(() => void poll(), 300000); } }
    };
    timer = setTimeout(() => void poll(), 200);
    const online = () => { if (controller && !controller.signal.aborted) controller.abort(); clearTimeout(timer); void poll(); };
    window.addEventListener('online', online);
    return () => { active = false; clearTimeout(timer); if (timeout !== null) clearTimeout(timeout); controller?.abort(); window.removeEventListener('online', online); };
    // Geometry/style changes redraw the same data, without additional requests.
  }, [source, layer.id, cache]);
  return <WeatherWidgetCanvas layer={layer} data={result?.source === source ? result.data : cachedWeatherPreview(cache, layer)} />;
}
